import {PrintOrientation} from './_models'

/** CSS pixels per millimetre (96 dpi), the unit the browser lays out and prints in. */
export const PX_PER_MM = 96 / 25.4

/**
 * A sheet in millimetres: A4 less 0.4 mm of height, so a sheet never spills a blank page out of
 * rounding (the same sizes as .ip-sheet in InvoicePrintPage.css).
 */
export const SHEET_SIZE: Record<PrintOrientation, {width: number; height: number}> = {
  portrait: {width: 210, height: 296.6},
  landscape: {width: 297, height: 209.6},
}

/**
 * Margins inside the sheet. The @page margin is zero (no browser headers or footers on the
 * paper), so these keep the content inside what office printers can print (about 4–5 mm).
 */
export const SHEET_PADDING_MM = {top: 8, right: 8, bottom: 7, left: 8}

/** Reserve against the difference between the measured screen layout and the printed one. */
export const PAGE_SAFETY_MM = 2

/** Font sizes tried, largest first, until the rows fit the sheet's width. */
export const FONT_STEPS_PT = [9, 8.5, 8, 7.5, 7, 6.5, 6]

/** In automatic orientation, portrait is kept while the rows fit at this size or larger. */
export const PORTRAIT_MIN_FONT_PT = 8

/**
 * Bumped whenever the printed layout changes: the server keeps rendered PDFs under this name, so a
 * new layout is printed afresh instead of being served from an older render.
 */
export const PRINT_LAYOUT_VERSION = 'p3-4'

export const sheetInnerSizePx = (orientation: PrintOrientation) => {
  const sheet = SHEET_SIZE[orientation]
  return {
    width: (sheet.width - SHEET_PADDING_MM.left - SHEET_PADDING_MM.right) * PX_PER_MM,
    height: (sheet.height - SHEET_PADDING_MM.top - SHEET_PADDING_MM.bottom) * PX_PER_MM,
  }
}

/** The @page rule for the chosen sheet; injected while the page is open. */
export const pageRule = (orientation: PrintOrientation) =>
  `@page { size: A4 ${orientation}; margin: 0; }`

/** The orientation and font sizes to try, in order, for a choice. */
export const layoutCandidates = (
  choice: 'auto' | PrintOrientation
): {orientation: PrintOrientation; fontPt: number}[] => {
  const steps = (orientation: PrintOrientation, minimum = 0) =>
    FONT_STEPS_PT.filter((size) => size >= minimum).map((fontPt) => ({orientation, fontPt}))
  if (choice === 'portrait') return steps('portrait')
  if (choice === 'landscape') return steps('landscape')
  return [...steps('portrait', PORTRAIT_MIN_FONT_PT), ...steps('landscape')]
}
